import { useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
	type DMSConnectionConfig,
	DMSProvider,
	type LightspeedDmsConnectionConfig,
	type OtherDmsConnectionConfig,
	Services,
} from '@ekhodealer/ekho-common';
import {
	Box,
	Form,
} from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
// eslint-disable-next-line no-restricted-imports -- intentional cross-feature: DMS is a shared concern
import { useCreateInventoryDmsConnection } from '@features/account-management/api/configuration/inventory/useCreateInventoryDmsConnection';

const DMS_PROVIDER_OPTIONS = [
	{ id: DMSProvider.LIGHTSPEED, label: 'Lightspeed DMS' },
	{ id: DMSProvider.OTHER, label: 'Other' },
];

const dmsConnectionSchema = z
	.object({
		dmsProvider: z.nativeEnum(DMSProvider, {
			required_error: 'Please select a DMS provider',
			invalid_type_error: 'Please select a DMS provider',
		}),
		cmf: z.string().optional(),
		otherDmsName: z.string().optional(),
		hasProvidedAuthConsent: z.boolean().refine((val) => val === true, {
			message: 'You must authorize Ekho to access your DMS data',
		}),
	})
	.refine(
		(data) => {
			if (data.dmsProvider === DMSProvider.LIGHTSPEED) {
				return data.cmf && data.cmf.trim().length > 0;
			}
			return true;
		},
		{ message: 'CMF number is required for Lightspeed DMS', path: ['cmf'] },
	)
	.refine(
		(data) => {
			if (data.dmsProvider === DMSProvider.OTHER) {
				return data.otherDmsName && data.otherDmsName.trim().length > 0;
			}
			return true;
		},
		{ message: 'DMS name is required', path: ['otherDmsName'] },
	);

type DmsConnectionFormData = z.infer<typeof dmsConnectionSchema>;

function getDefaultValues(
	dmsConnection: DMSConnectionConfig | undefined,
): Partial<DmsConnectionFormData> {
	if (!dmsConnection) {
		return { dmsProvider: undefined, cmf: '', otherDmsName: '', hasProvidedAuthConsent: false };
	}
	const { dmsProvider, connectionDetails, hasProvidedAuthConsent } = dmsConnection;
	return {
		dmsProvider,
		cmf:
			dmsProvider === DMSProvider.LIGHTSPEED
				? (connectionDetails as LightspeedDmsConnectionConfig).cmf ?? ''
				: '',
		otherDmsName:
			dmsProvider === DMSProvider.OTHER
				? (connectionDetails as OtherDmsConnectionConfig).name ?? ''
				: '',
		hasProvidedAuthConsent: hasProvidedAuthConsent ?? false,
	};
}

function getConfigFromFormData(data: DmsConnectionFormData): DMSConnectionConfig {
	if (data.dmsProvider === DMSProvider.OTHER) {
		return {
			dmsProvider: data.dmsProvider,
			connectionDetails: { name: data.otherDmsName!.trim() },
			hasProvidedAuthConsent: data.hasProvidedAuthConsent,
		};
	}
	return {
		dmsProvider: data.dmsProvider,
		connectionDetails: { cmf: data.cmf!.trim() },
		hasProvidedAuthConsent: data.hasProvidedAuthConsent,
	};
}

function DmsFormFields(): React.JSX.Element {
	const dmsProvider = useWatch({ name: 'dmsProvider' });

	return (
		<>
			<Form.Field.Dropdown
				schemaField='dmsProvider'
				labelText='DMS Provider'
				required
				options={DMS_PROVIDER_OPTIONS}
				placeholder='Select DMS provider'
				buttonVariant='outline'
			/>
			{dmsProvider === DMSProvider.LIGHTSPEED && (
				<Form.Field.Text
					schemaField='cmf'
					labelText='CMF Number'
					descriptionText='Your Customer Master File number from Lightspeed'
					required
					size='large'
					placeholder='Enter CMF number'
				/>
			)}
			{dmsProvider === DMSProvider.OTHER && (
				<Form.Field.Text
					schemaField='otherDmsName'
					labelText='DMS Name'
					descriptionText='Enter the name of your DMS provider'
					required
					size='large'
					placeholder='Enter DMS name'
				/>
			)}
			<Form.Field.Checkbox
				schemaField='hasProvidedAuthConsent'
				labelText='I authorize Ekho to access my DMS data'
				required
			/>
		</>
	);
}

export function DmsConnectionSection(): React.JSX.Element {
	const { data: provider } = useCurrentServicesProvider();
	const createDmsMutation = useCreateInventoryDmsConnection();

	const dmsConnection = provider?.config?.[Services.INVENTORY]?.dmsConnectionConfig;

	const handleSubmit = async (data: DmsConnectionFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await createDmsMutation.mutateAsync({
				dmsConnectionConfig: getConfigFromFormData(data),
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='integrations-dms-connection'
				schema={dmsConnectionSchema}
				defaultValues={getDefaultValues(dmsConnection)}
				onSubmit={handleSubmit}
				showSaveAndClear
				title='DMS connection'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Connect your Dealer Management System to sync inventory and customer data.'>
				<DmsFormFields />
			</Form>
		</Box>
	);
}

// --- DMS sync settings (coming soon) ---

const dmsSyncSchema = z.object({
	syncFrequency: z.string().optional(),
	autoArchive: z.boolean().optional(),
});

export function DmsSyncSettingsSection(): React.JSX.Element {
	return (
		<Box padding='24' background='secondary' rounding='12'>
			<div data-settings-disabled>
				<Form
					name='integrations-dms-sync-settings'
					schema={dmsSyncSchema}
					defaultValues={{ syncFrequency: undefined, autoArchive: false }}
					onSubmit={async () => ({ isSuccess: false })}
					title='Sync settings'
					titleLevel='subgroup'
					titleWeight='semibold'
					titleMaxWidth='full'
					description='Control how often inventory and customer data is synced from your DMS, and whether stale records are automatically archived.'
					tag={{ children: 'Coming soon', size: 'small' }}>
					<Form.Field.Text
						schemaField='syncFrequency'
						labelText='Sync frequency'
						descriptionText='How often Ekho pulls updated data from your DMS.'
						size='large'
						placeholder='E.g., Every 6 hours'
					/>
					<Form.Field.Switch
						schemaField='autoArchive'
						labelText='Auto-archive stale records'
						descriptionText='Automatically archive inventory records that are no longer present in your DMS feed.'
						orientation='horizontal'
					/>
				</Form>
			</div>
		</Box>
	);
}
